import React, {useCallback, useEffect} from 'react';
import styled from 'styled-components';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {Col, Form} from 'react-bootstrap';
import {Size} from 'shared/components/Forms/Size';
import {useDrawerPosition} from 'components/customer/Product/Drawer/helpers/useDrawerPosition';
import {useFaceDraft} from 'components/customer/Product/Drawer/FaceDraftContext';
import {DragLimits} from 'components/customer/Product/Drawer/helpers/useInnerDrawerDrag';

interface DrawerPositionProps {
    index: number;
    // half-typed text goes to the shared draft, for a layout that also shows
    // the position on the drawing; this field is then the one that commits it
    sharesDraft?: boolean;
    limitsOf?: (index: number) => DragLimits;
}

export const DrawerPosition = ({
    index,
    sharesDraft = false,
    limitsOf,
}: DrawerPositionProps) => {
    const {name, positionValue, isInvalid, updatePositionToBottom} =
        useDrawerPosition(index, limitsOf);
    const {textFor, setDraft, clearDraft, registerCommit, commit} =
        useFaceDraft();
    const draft = sharesDraft ? textFor(index) : null;

    const typeHandler = useCallback(
        (field: string, next: number) => {
            if (sharesDraft) {
                setDraft(index, String(next ?? ''));

                return;
            }

            updatePositionToBottom(field, next);
        },
        [index, setDraft, sharesDraft, updatePositionToBottom]
    );

    // every commit of a shared draft comes here, whichever box it was typed in
    const commitText = useCallback(
        (text: string) => {
            clearDraft();

            if (text === null) return;

            const parsed = parseFloat(String(text).trim());
            const next = Number.isFinite(parsed) ? parsed : 0;

            // leaving it untouched is not an edit, and would mark it moved
            if (next === positionValue) return;

            updatePositionToBottom(name, next);
        },
        [clearDraft, name, positionValue, updatePositionToBottom]
    );

    useEffect(
        () => (sharesDraft ? registerCommit(index, commitText) : undefined),
        [sharesDraft, index, commitText, registerCommit]
    );

    const blurHandler = useCallback(() => {
        if (sharesDraft) commit(index);
    }, [sharesDraft, commit, index]);

    return (
        <FormGroup>
            <Form.Label column xs={5}>
                Position to bottom
            </Form.Label>
            <Col xs={7}>
                <Control onBlur={blurHandler}>
                    <Size
                        isInvalid={isInvalid}
                        name={name}
                        value={draft === null ? positionValue : draft}
                        metric="mm"
                        selectHandler={typeHandler}
                        fieldSetIndex={index}
                        liveUpdate={sharesDraft}
                    />
                </Control>
            </Col>
        </FormGroup>
    );
};

const Control = styled.div`
    > .input-group {
        flex: 1;
    }
`;
